import { z } from 'zod';
import { LotStatus, OfferStatus } from './types.js';
// Asset schemas
const tonAssetSchema = z.object({
    kind: z.literal('TON'),
    amountNano: z.string().regex(/^\d+$/, 'Must be a valid nano amount'),
});
const jettonAssetSchema = z.object({
    kind: z.literal('JETTON'),
    master: z.string().min(1, 'Jetton master address required'),
    amount: z.string().regex(/^\d+$/, 'Must be a valid amount'),
});
const nftAssetSchema = z.object({
    kind: z.literal('NFT'),
    address: z.string().min(1, 'NFT address required'),
});
const tgNumberAssetSchema = z.object({
    kind: z.literal('TG_NUMBER'),
    address: z.string().min(1, 'Telegram number NFT address required'),
});
const tgUsernameAssetSchema = z.object({
    kind: z.literal('TG_USERNAME'),
    address: z.string().min(1, 'Telegram username NFT address required'),
});
const dnsAssetSchema = z.object({
    kind: z.literal('DNS'),
    address: z.string().min(1, 'DNS domain NFT address required'),
});
const tgGiftAssetSchema = z.object({
    kind: z.literal('TG_GIFT'),
    address: z.string().min(1, 'Telegram Gift NFT address required'),
});
export const assetSchema = z.discriminatedUnion('kind', [
    tonAssetSchema,
    jettonAssetSchema,
    nftAssetSchema,
    tgGiftAssetSchema,
    tgNumberAssetSchema,
    tgUsernameAssetSchema,
    dnsAssetSchema,
]);
export const assetKindSchema = z.enum([
    'TON',
    'JETTON',
    'NFT',
    'TG_GIFT',
    'TG_NUMBER',
    'TG_USERNAME',
    'DNS',
]);
// Lot and Offer schemas
export const lotStatusSchema = z.nativeEnum(LotStatus);
export const offerStatusSchema = z.nativeEnum(OfferStatus);
export const lotSchema = z.object({
    lotId: z.number().int().nonnegative(),
    sellerAddress: z.string().min(1),
    assets: z.array(assetSchema).min(1, 'At least one asset required'),
    wishlistText: z.string().max(1000, 'Wishlist text too long'),
    status: lotStatusSchema,
    isPublic: z.boolean().default(true),
    reservedFor: z.string().nullable().default(null),
    buyNowTon: z.string().nullable().default(null),
    createdAt: z.date(),
    expiresAt: z.date(),
    txHash: z.string(),
    txLt: z.string(),
});
export const offerSchema = z.object({
    offerId: z.number().int().nonnegative(),
    lotId: z.number().int().nonnegative(),
    buyerAddress: z.string().min(1),
    assets: z.array(assetSchema).min(1, 'At least one asset required'),
    status: offerStatusSchema,
    createdAt: z.date(),
    expiresAt: z.date(),
    txHash: z.string(),
    txLt: z.string(),
});
// API request schemas
export const createLotRequestSchema = z.object({
    assets: z.array(assetSchema).min(1, 'At least one asset required'),
    wishlistText: z.string().max(1000, 'Wishlist text too long').default(''),
    expiresAt: z.coerce.date(),
});
export const makeOfferRequestSchema = z.object({
    lotId: z.number().int().nonnegative(),
    assets: z.array(assetSchema).min(1, 'At least one asset required'),
    expiresAt: z.coerce.date(),
});
// Reusable primitives
export const tonAddressSchema = z.string().min(48).max(67);
export const lotIdParamSchema = z.object({ lotId: z.coerce.number().int().nonnegative() });
export const addressParamSchema = z.object({ address: tonAddressSchema });
// Query parameter schemas
export const lotsQuerySchema = z.object({
    query: z.string().max(200).optional(),
    assetKind: assetKindSchema.optional(),
    jettonMaster: z.string().optional(),
    collection: z.string().optional(),
    collectionName: z.string().max(200).optional(),
    giftModel: z.string().max(200).optional(),
    status: lotStatusSchema.optional(),
    seller: z.string().optional(),
    isPublic: z.coerce.boolean().optional(),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(20),
    sort: z.enum(['createdAt', 'expiresAt', 'offerCount', 'buyNow']).default('createdAt'),
    order: z.enum(['asc', 'desc']).default('desc'),
});
export const offersQuerySchema = z.object({
    lotId: z.coerce.number().int().nonnegative().optional(),
    buyer: z.string().optional(),
    status: offerStatusSchema.optional(),
});
export const activityQuerySchema = z.object({
    limit: z.coerce.number().int().positive().max(50).default(20),
});
// Request body schemas
export const registerProfileSchema = z.object({
    walletAddress: tonAddressSchema,
    telegramUserId: z.number().int().positive(),
    telegramChatId: z.number().int().positive().optional(),
});
export const notificationPrefsSchema = z.object({
    offers: z.boolean().optional(),
    accepts: z.boolean().optional(),
    rejects: z.boolean().optional(),
    expiry: z.boolean().optional(),
});
export const featureKeyParamSchema = z.object({
    key: z.string().min(1).max(100).regex(/^[a-z0-9][a-z0-9-]*$/, 'Invalid feature key'),
});
export const featureVoteSchema = z.object({
    walletAddress: tonAddressSchema,
});
export const feedbackSchema = z.object({
    message: z.string().min(2).max(500),
    mood: z.string().max(20).optional(),
    walletAddress: tonAddressSchema.optional(),
    page: z.string().max(100).optional(),
});
export const featureSuggestSchema = z.object({
    title: z.string().min(3).max(100),
    walletAddress: tonAddressSchema,
});
export const referralTrackSchema = z.object({
    referrerAddress: tonAddressSchema,
    referredAddress: tonAddressSchema,
});
export const batchAddressesSchema = z.object({
    addresses: z.string().min(1),
});
export const metadataJettonParamSchema = z.object({
    master: z.string().min(10),
});
export const metadataJettonsBatchSchema = z.object({
    masters: z.string().min(1),
});
export const notificationReadSchema = z.object({
    ids: z.array(z.coerce.number().int().positive()).max(100).optional(),
});
export const adminQuerySchema = z.object({
    password: z.string().optional(),
    days: z.coerce.number().int().positive().max(365).default(30),
});
export const adminTicketFilterSchema = z.object({
    password: z.string().optional(),
    status: z.enum(['open', 'in_progress', 'resolved', 'closed']).default('open'),
});
export const adminDashboardQuerySchema = z.object({
    password: z.string().optional(),
    days: z.coerce.number().int().positive().max(90).default(14),
    tab: z.enum(['overview', 'funnel', 'sentiment', 'revenue', 'ops']).default('overview'),
});
export const adminTicketUpdateSchema = z.object({
    status: z.enum(['open', 'in_progress', 'resolved', 'closed']).optional(),
    response: z.string().max(2000).optional(),
});
// Saved searches (watchlist)
export const savedSearchQueryParamsSchema = z.object({
    query: z.string().max(200).optional(),
    assetKind: assetKindSchema.optional(),
    jettonMaster: z.string().optional(),
    collection: z.string().optional(),
    sort: z.enum(['createdAt', 'expiresAt', 'offerCount', 'buyNow']).optional(),
    order: z.enum(['asc', 'desc']).optional(),
});
export const createSavedSearchSchema = z.object({
    name: z.string().min(1, 'Name is required').max(100),
    queryParams: savedSearchQueryParamsSchema,
    notifyEnabled: z.boolean().default(false),
});
export const updateSavedSearchSchema = z.object({
    name: z.string().min(1).max(100).optional(),
    notifyEnabled: z.boolean().optional(),
});
export const savedSearchIdParamSchema = z.object({
    searchId: z.coerce.number().int().positive(),
});
// Want Ads (reverse listings)
export const createWantAdSchema = z.object({
    title: z.string().min(3, 'Title must be at least 3 characters').max(120),
    description: z.string().max(1000).default(''),
    budgetTonNano: z.string().optional(),
    assetKinds: z.array(assetKindSchema).min(1, 'Select at least one asset type'),
    preferredCollections: z.array(z.string().max(200)).max(10).default([]),
    preferredAttributes: z.record(z.string(), z.string().max(200)).optional(),
    expiresInDays: z.coerce.number().int().min(1).max(30).default(7),
});
export const wantAdsQuerySchema = z.object({
    status: z.enum(['OPEN', 'FULFILLED', 'CLOSED', 'EXPIRED']).optional(),
    assetKind: assetKindSchema.optional(),
    query: z.string().max(200).optional(),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().min(1).max(50).default(20),
});
export const wantAdIdParamSchema = z.object({
    adId: z.coerce.number().int().positive(),
});
// Watchers (B-400)
export const watchLotSchema = z.object({
    address: tonAddressSchema,
});
// Auto-accept/decline thresholds (B-404)
export const lotThresholdsSchema = z.object({
    autoAcceptTon: z.string().regex(/^\d+$/, 'Must be nanotons').nullable().optional(),
    autoDeclineTon: z.string().regex(/^\d+$/, 'Must be nanotons').nullable().optional(),
});
//# sourceMappingURL=schemas.js.map